import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useRouteError,
} from "react-router";
import { ThemeProvider } from "next-themes";
import { QueryClientProvider } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { Toaster } from "@/components/ui/sonner";
import { TranslationProvider, translationStore, useTranslation } from "@/i18n";
import { StoreProvider } from "@/stores/store-context";
import { RouteProgressBar } from "@/components/route-progress-bar";
import { RouterSync } from "@/components/router-sync";
import { VersionWatcher } from "@/components/version-watcher";
import { SentryUserSync } from "@/components/sentry-user-sync";
import { UserpilotUserSync } from "@/components/userpilot-user-sync";
import { SystemRoleVocabularySync } from "@/components/system-role-vocabulary-sync";
import { ProfileSettingsModal } from "@/components/profile-settings/profile-settings-modal";
import { sentryIsEnabled } from "@/sentry";
import { getQueryClient } from "@/queries/client";
import { QueryDevtools } from "@/queries/devtools";
import appStylesheetUrl from "./app.css?url";
import { observer } from "mobx-react";

export const links = () => [{ rel: "stylesheet", href: appStylesheetUrl }];

export const Layout = observer(function Layout({ children }: { children: React.ReactNode }) {
  // Layout renders outside <TranslationProvider>, which is fine: the context's
  // default value *is* the singleton translation store.
  const { locale } = useTranslation();
  return (
    <html lang={locale} suppressHydrationWarning>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="icon" type="image/png" sizes="32x32" href="/favicon/favicon-32x32.png" />
        <link rel="icon" type="image/png" sizes="16x16" href="/favicon/favicon-16x16.png" />
        <link rel="shortcut icon" href="/favicon.ico" />
        <Meta />
        <Links />
      </head>
      <body className="min-h-screen bg-background text-foreground antialiased">
        <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
          {children}
          <Toaster position="bottom-right" richColors />
        </ThemeProvider>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
});

const Root = observer(function Root() {
  return (
    <TranslationProvider>
      {/* The loaders' client, so a loader-primed entry is the one a component reads. */}
      <QueryClientProvider client={getQueryClient()}>
        <StoreProvider>
          <RouterSync />
          <VersionWatcher />
          <SentryUserSync />
          <UserpilotUserSync />
          <SystemRoleVocabularySync />
          <RouteProgressBar />
          <Outlet />
          <ProfileSettingsModal />
        </StoreProvider>
        <QueryDevtools />
      </QueryClientProvider>
    </TranslationProvider>
  );
});

export default Root;

export const HydrateFallback = observer(function HydrateFallback() {
  return (
    <div className="flex h-screen items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  );
});

export const ErrorBoundary = observer(function ErrorBoundary() {
  const error = useRouteError();
  if (!isRouteErrorResponse(error) && sentryIsEnabled()) {
    Sentry.captureException(error);
  }
  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6 text-foreground">
      <div className="max-w-md space-y-2 text-center">
        <h1 className="text-2xl font-semibold">{translationStore.t("error_boundary.title")}</h1>
        <p className="text-muted-foreground">{translationStore.t("error_boundary.description")}</p>
      </div>
    </div>
  );
});
